import { Box, Button, Chip, Grid, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { Save } from '@mui/icons-material';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import {
  addOutOfStockItem,
  allOutOfStockProductsCacheKey,
  removeOutOfStock,
  updateOutOfStockItem,
  useAllOutOfStockProducts,
  useAllProducts,
  useProduct,
} from '../../apis/products-api';
import { OutOfStockProductDto, ProductDto } from '@kotipizzagroup/kotipizza-products-api-client';
import { isScheduleInEffect } from '../../shared/utils/outOfStockFilters';
import LoadingOverlay from '../../shared/LoadingOverlay/LoadingOverlay';
import { SearchInput } from '../../shared/Search/SearchInput';
import { GridColDef } from '@mui/x-data-grid';
import DataGrid from '../../shared/DataGrid/StyledDataGrid';
import { useCustomSnackBar } from '../../shared/SnackBar/useCustomSnackBar';
import { useQueryClient } from '@tanstack/react-query';
import { OutOfStockRow } from '.';
import { BottomBar, TopBar } from '../ProductsPage/ProductDetails';
import OutOfStockScheduleSelect from './OutOfStockScheduleSelect';
import dayjs from 'dayjs';
import { useAllRestaurantsWithDetails } from '../../apis/shops-api';
import { UserRole } from '@kotipizzagroup/kotipizza-users-api-client';
import {
  CAMPAIGNS_WRITE,
  LOYALTY_WRITE,
  PRODUCTS_WRITE,
  SHOPS_WRITE,
  usePermission,
} from '../../shared/utils/permissions';
import { useUserPermissions } from '../../apis/user-details';

type OutOfStockProductFormData = {
  outOfStocks: OutOfStockProductDto[];
};

export const OutOfStockProductsDetails = () => {
  const { id } = useParams<{ id?: string }>();
  const { shops: authorizedShopIds } = useUserPermissions();

  const [productId, setProductId] = useState(isNaN(Number(id)) ? 0 : Number(id));
  const [isSaving, setIsSaving] = useState(false);

  const { t } = useTranslation();
  const snackBar = useCustomSnackBar();
  const queryClient = useQueryClient();

  const isNew = productId === 0;

  const isAdminUser = usePermission([PRODUCTS_WRITE, LOYALTY_WRITE, SHOPS_WRITE, CAMPAIGNS_WRITE]);
  const userAuthorizesToCreateOutOfStock = usePermission([UserRole.OutOfStock_CanCreate]) || isAdminUser;
  const userAuthorizesToUpdateOutOfStock = usePermission([UserRole.OutOfStock_CanUpdate]) || isAdminUser;
  const userAuthorizesToDeleteOutOfStock = usePermission([UserRole.OutOfStock_CanDelete]) || isAdminUser;

  const createNewOutOfStockProduct = (restaurantId: string): OutOfStockProductDto => ({
    productId,
    externalShopId: restaurantId,
  });

  const { handleSubmit, control, reset } = useForm<OutOfStockProductFormData>({
    defaultValues: {
      outOfStocks: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'outOfStocks',
  });

  const appendOutOfStockProduct = (product: OutOfStockProductDto) => {
    const restaurantFoundFromList = fields.find((f) => f.externalShopId === product.externalShopId);
    if (restaurantFoundFromList) {
      if (dayjs(restaurantFoundFromList.scheduledUntil) <= dayjs()) {
        const index = fields.findIndex(
          (f) => f.externalShopId === product.externalShopId && f.productId === product.productId
        );
        update(index, product);
        return;
      }
      snackBar.showError(t('Restaurant is added already'));
      return;
    }
    append(product);
  };

  const updateOutOfStockProduct = (product: OutOfStockProductDto) => {
    const index = fields.findIndex(
      (f) => f.externalShopId === product.externalShopId && f.productId === product.productId
    );
    update(index, product);
  };

  const productColumns: GridColDef<ProductDto>[] = [
    {
      field: 'name',
      headerName: t('Name'),
      flex: 1,
      renderCell: (params) => {
        return params.value;
      },
    },
    {
      field: 'productCategoryName',
      headerName: t('Product category'),
      flex: 1,
      renderCell: (params) => {
        return params.value;
      },
    },
    {
      field: 'active',
      headerName: t('Status'),
      flex: 1,
      renderCell: (params) => {
        return params.value ? (
          <Chip color="primary" label={t('shared.activeLabel.active')} />
        ) : (
          <Chip color="error" label={t('shared.activeLabel.inactive')} />
        );
      },
    },
  ];

  const handleScheduleChange = (date: string | null, productId: number, shopExternalId: string) => {
    updateOutOfStockProduct({
      productId,
      externalShopId: shopExternalId,
      scheduledUntil: date,
    });
  };

  const restaurantColumns: GridColDef<OutOfStockRow>[] = [
    {
      field: 'externalShopId',
      headerName: 'Id',
      renderCell: (params) => {
        return params.value;
      },
    },
    {
      field: 'name',
      headerName: t('Name'),
      flex: 1,
      renderCell: (params) => {
        if (!params.row.restaurantName) return null;
        return params.row.restaurantName;
      },
    },
    {
      field: 'scheduledUntil',
      headerName: t('Scheduled until'),
      flex: 1,
      renderCell: (params) => {
        if (!params.row.item.scheduledUntil) return null;
        return dayjs(params.row.item.scheduledUntil).format('D.M.YYYY');
      },
    },
    {
      field: 'schedule',
      headerName: t('Schedule'),
      width: 200,
      renderCell: (params) => {
        return (
          userAuthorizesToUpdateOutOfStock && (
            <OutOfStockScheduleSelect
              value={params.row.item.scheduledUntil}
              allowIndefinite={!shopIdsLockedToSchedule.has(params.row.item.externalShopId)}
              onChange={(date) => handleScheduleChange(date, params.row.itemId, params.row.item.externalShopId)}
            />
          )
        );
      },
    },
    {
      field: 'remove',
      headerName: t('Unlink'),
      width: 200,
      renderCell: (params) => {
        const index = fields.findIndex((f) => f.externalShopId === params.row.item.externalShopId);

        return (
          userAuthorizesToDeleteOutOfStock && (
            <Button color="error" variant="outlined" onClick={() => remove(index)}>
              {t('Unlink')}
            </Button>
          )
        );
      },
    },
  ];

  const {
    data: product,
    isInitialLoading: isInitialLoadingProduct,
    isFetching: isFetchingProduct,
  } = useProduct(productId, { enabled: !isNew });
  const { data: outOfStockProducts, isInitialLoading: isOutOfStockProductsInitialLoading } = useAllOutOfStockProducts();
  const { data: products, isInitialLoading: isInitialLoadingProducts } = useAllProducts(false);
  const { data: restaurants, isInitialLoading: isInitialLoadingRestaurants } = useAllRestaurantsWithDetails();

  const authorizedShops = useMemo(() => {
    return isAdminUser ? restaurants : restaurants?.filter((r) => authorizedShopIds.includes(r.externalId));
  }, [authorizedShopIds, isAdminUser, restaurants]);

  const outOfStocks = useMemo(() => {
    return outOfStockProducts?.filter((p) => p.productId === productId) || [];
  }, [outOfStockProducts, productId]);

  /**
   * Restaurants whose stored entry already has a return date. The update endpoint cannot clear a
   * schedule back to open-ended, so these rows - including expired ones being marked out of stock
   * again - may only be given a new date, never "Indefinitely".
   */
  const shopIdsLockedToSchedule = useMemo(
    () => new Set(outOfStocks.filter((p) => p.scheduledUntil).map((p) => p.externalShopId)),
    [outOfStocks]
  );

  const restaurantRows = useMemo((): OutOfStockRow[] => {
    return fields.filter(isScheduleInEffect).map((f) => ({
      ...f,
      item: f,
      itemId: f.productId,
      restaurantName: authorizedShops?.find((r) => r.externalId === f.externalShopId)?.displayName,
    }));
  }, [fields, authorizedShops]);

  const isInitialLoadComplete =
    !isInitialLoadingProduct &&
    !isInitialLoadingProducts &&
    !isOutOfStockProductsInitialLoading &&
    !isInitialLoadingRestaurants;

  useEffect(() => {
    if (!isInitialLoadComplete) return;
    reset({ outOfStocks: outOfStockProducts?.filter((p) => p.productId == productId) });
  }, [isInitialLoadComplete, outOfStockProducts, productId, reset]);

  const isSaveDisabled = isNew || isFetchingProduct;

  const handleProductSave: SubmitHandler<OutOfStockProductFormData> = async (formData) => {
    // A stored entry with a return date can only be given a new date, so re-marking one out of stock
    // takes effect only once a date is picked. Saving it with an empty schedule would do nothing at
    // all, so say that instead of reporting a save that did not happen.
    const rowsMissingSchedule = formData.outOfStocks.filter(
      (item) => shopIdsLockedToSchedule.has(item.externalShopId) && !item.scheduledUntil
    );
    if (rowsMissingSchedule.length > 0) {
      const restaurantNames = rowsMissingSchedule
        .map(
          (item) =>
            authorizedShops?.find((r) => r.externalId === item.externalShopId)?.displayName ?? item.externalShopId
        )
        .join(', ');
      snackBar.showError(t('Select return date for: {{restaurants}}', { restaurants: restaurantNames }));
      return;
    }

    try {
      setIsSaving(true);

      for (const item of formData.outOfStocks) {
        // Find and post new items
        const itemExists = outOfStocks?.find((p) => p.externalShopId === item.externalShopId);
        if (!itemExists && item.productId && item.externalShopId) {
          await addOutOfStockItem({
            productId: item.productId,
            externalShopId: item.externalShopId,
            scheduledUntil: item.scheduledUntil,
          });
        }
        // Update items when the schedule changed to a new date. Clearing a schedule back to
        // open-ended is not supported here: the update endpoint requires a non-null scheduledUntil.
        if (
          itemExists &&
          item.productId &&
          item.externalShopId &&
          item.scheduledUntil &&
          item.scheduledUntil !== itemExists.scheduledUntil
        ) {
          await updateOutOfStockItem({
            productId: item.productId,
            externalShopId: item.externalShopId,
            scheduledUntil: item.scheduledUntil,
          });
        }
      }

      // Remove items
      for (const item of outOfStocks) {
        const shouldKeep = formData.outOfStocks.find((p) => p.externalShopId === item.externalShopId);
        if (!shouldKeep && item.productId && item.externalShopId) {
          await removeOutOfStock({ productId: item.productId, externalShopId: item.externalShopId });
        }
      }

      setIsSaving(false);
      await queryClient.invalidateQueries([allOutOfStockProductsCacheKey]);
      snackBar.showSuccess(t('global.saveSucceeded'));
    } catch {
      snackBar.showError(t('global.saveFailed'));
      setIsSaving(false);
    }
  };

  const saveButton = (
    <LoadingButton
      onClick={handleSubmit(handleProductSave)}
      startIcon={<Save />}
      loading={isSaving}
      disabled={isSaveDisabled}
      variant="contained"
      color="primary"
    >
      {t('global.save')}
    </LoadingButton>
  );

  const handleAddRestaurant = (value: string) => {
    appendOutOfStockProduct(createNewOutOfStockProduct(value));
  };

  const handleSelectProduct = (value: string) => {
    setProductId(Number(value));
  };

  const productList: ProductDto[] = useMemo(() => {
    if (!product) return [];
    return [{ ...product }];
  }, [product]);

  return (
    <Box>
      {!isInitialLoadComplete && <LoadingOverlay />}
      <TopBar>
        <Typography variant="h1">
          {t('Out of stock products')} / {t('Mark as out of stock')}
        </Typography>
        {userAuthorizesToUpdateOutOfStock && saveButton}
      </TopBar>
      <Grid container item justifyContent="space-between" alignItems="center" my={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="h2">{t('Out of stock product')}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          {isNew && (
            <SearchInput
              values={products?.map((p) => ({ value: String(p.productId), label: p.name })) || []}
              noLabelPrefix={true}
              onSelect={handleSelectProduct}
              label={t('Search product')}
            />
          )}
        </Grid>
      </Grid>
      {product && (
        <DataGrid
          disableRowSelectionOnClick
          rows={productList}
          columns={productColumns}
          getRowId={(row) => row.productId || ''}
          loading={false}
          hideFooter={true}
        />
      )}

      {!isNew && (
        <div>
          <Grid container item justifyContent="space-between" alignItems="center" my={2} marginTop={6}>
            <Grid item xs={12} md={4}>
              <Typography variant="h2">{t('Out of stock from restaurant')}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              {userAuthorizesToCreateOutOfStock && (
                <SearchInput
                  values={authorizedShops?.map((r) => ({ value: r.externalId, label: r.displayName })) || []}
                  onSelect={handleAddRestaurant}
                  noLabelPrefix={true}
                  label={t('Search restaurant')}
                />
              )}
            </Grid>
          </Grid>
          <DataGrid
            disableRowSelectionOnClick
            rows={restaurantRows}
            columns={restaurantColumns}
            getRowId={(row) => row.item.externalShopId}
            loading={false}
          />
        </div>
      )}

      <Grid item xs={12} container marginTop={8}>
        {userAuthorizesToUpdateOutOfStock && saveButton}
      </Grid>
      <BottomBar />
    </Box>
  );
};
